<template>
  <div class="MapBox">
    <MglMap
      class="MapBox__wrapper" 
      :accessToken="accessToken"
      :mapStyle.sync="mapStyle"
      @load="onMapLoaded">
      <ChargingPointPlanner 
        v-if="planMode" />
    </MglMap>
    
    <TileData
      class="MapBox__data"
      :loaded="loaded" />

  </div>
</template>

<script>
import { MglMap } from 'vue-mapbox'
import mapboxgl from 'mapbox-gl'

import TileData from 'molecule/TileData'
import ChargingPointPlanner from 'molecule/ChargingPointPlanner'

import { mapGetters, mapMutations } from 'vuex'

import { accessToken, mapStyle } from 'config/mapbox'
import { icon, image } from 'helper/assets'


import { 
  decielen
} from 'config/inkomensdecielen'

export default {
  components: {
    MglMap, TileData, ChargingPointPlanner
  },
  data() {
    return {
      loaded: false,
      accessToken,
      mapStyle
    }
  },
  computed: {
    ...mapGetters('map', [
      'getForecastStatus',
      'getHighlighted',
      'getSymbolLayerId'
    ]),
    ...mapGetters('config', {
      planMode: 'planmode',
      layerConfig: 'layers'
    }),
    // The paint instruction for the hexagons
    fillHexagons() {
      let value = this.forecastFilterSwitch({ 
        forecast: this.getForecastStatus.forecast 
      })
      
      return [
        "case",
        // The selected tile
        [
          "==",
          [
            "get",
            "fid"
          ],
          this.getHighlighted
        ],
        "hsl(23, 100%, 42%)",
        // The layer we want to ignore
        [
          "<",
          value,
          0.05
        ],
        "hsla(0, 0%, 0%, 0)",
        // 0 - 1
        [
          "<=",
          value,
          6
        ],
        "hsl(213, 50%, 80%)",
        // 1+ - 2
        [
          "<=",
          value,
          12
        ],
        "hsl(213, 65%, 55%)",
        // 2+ - 4
        [
          "<=",
          value,
          24
        ],
        "hsl(213, 80%, 35%)",
        // 4+
        [
          ">",
          value,
          24
        ],
        "hsl(213, 95%, 20%)",
        // Default (required by MapBox)
        "hsla(0, 0%, 0%, 0)"
      ]
    }
  },
  watch: {
    getForecastStatus: {
      handler: function() {
        // let state = (value.access === 'public') 
        //   ? 'visible'
        //   : 'none';
        // this.$store.map.setLayoutProperty('trees', 'visibility', state)
        this.$store.map.setPaintProperty('hexagons', 'fill-color', this.fillHexagons)
      },
      deep: true
    },
    getHighlighted: function() {
      this.$store.map.setPaintProperty('hexagons', 'fill-color', this.fillHexagons)
    }
  },
  methods: {
    ...mapMutations('map', [
      'setLoadedStatus',
      'setLayerAvailability',
      'setSymbolLayerId'
    ]),
    icon,
    image,
    onMapLoaded(event) {
      // Note: a reference to the map has to be stored in a non-reactive manner.
      this.$store.map = event.map

      let nav = new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true
      });
      this.$store.map.addControl(nav, 'top-right');

      this.addLayers()

      // Signal to the TileData component that the map is done loading
      this.loaded = true
      this.setLoadedStatus({ status: true })
    },
    addLayers() {
      
      // We use this to add our layers below text labels
      let firstSymbolId = this.getFirstSymbolLayerId()
      this.setSymbolLayerId({ id: firstSymbolId })

      // The area border
      if (this.layerConfig.border.active) {
        this.$store.map.addSource('border', {
          type: 'vector',
          url: this.layerConfig.border.url
        })
        this.$store.map.addLayer({ 
          "id": "border",
          "type": "fill",
          "source": "border",
          "source-layer": this.layerConfig.border.source,
          "paint": {
            "fill-color": "hsla(0, 0%, 0%, 0)",
            "fill-opacity": 1,
            "fill-outline-color": "hsla(23,100%, 62%, 1)"
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'border', status: true })
      }

      // The main layer: the hexagons
      if (this.layerConfig.hexagons.active) {
        this.$store.map.addSource('hexagons', {
          type: 'vector',
          url: this.layerConfig.hexagons.url
        })
        this.$store.map.addLayer({ 
          "id": "hexagons",
          "type": "fill",
          "source": "hexagons",
          "source-layer": this.layerConfig.hexagons.source,
          "paint": {
            "fill-color": this.fillHexagons,
            // Note: The legend opacity is linked
            "fill-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              10,
              0.7,
              13,
              0.4
            ],
            "fill-outline-color": [
              "interpolate",
              ["linear"],
              ["zoom"],
              10,
              "hsla(232, 67%, 60%, 0)",
              22,
              "hsla(232, 67%, 60%, 0.9)"
            ]
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'hexagons', status: true })
      }

      // Lichtnet
      if (this.layerConfig.ls.active) {
        this.$store.map.addSource('ls', {
          type: 'vector',
          url: this.layerConfig.ls.url
        })
        this.$store.map.addLayer({ 
          "id": "ls",
          "type": "line",
          "source": "ls",
          "source-layer": this.layerConfig.ls.source,
          "minzoom": 13,
          "paint": {
            "line-color": "hsl(144, 79%, 57%)", //"hsla(23,100%, 62%, 0.7)", // "hsl(52, 73%, 73%)",
            "line-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              0,
              14,
              0.90
            ]
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'ls', status: true })
      }

      // Openbare Verlichting
      if (this.layerConfig.ovkasten.active) {
        this.$store.map.addSource('ovkasten', {
          type: 'vector',
          url: this.layerConfig.ovkasten.url
        })
        this.$store.map.addLayer({ 
          "id": "ovkasten",
          "type": "circle",
          "source": "ovkasten",
          "source-layer": this.layerConfig.ovkasten.source,
          "minzoom": 13,
          "paint": {
            'circle-radius': [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              1,
              22,
              16
            ],
            "circle-color": "hsl(93, 28%, 43%)",
            "circle-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              0,
              14,
              0.72
            ],
            "circle-stroke-width": 0,
          }
        }, firstSymbolId)
        this.$store.map.addLayer({
          "id": "ovkasten-labels",
          "type": "symbol",
          "source": "ovkasten",
          "source-layer": this.layerConfig.ovkasten.source,
          "layout": {
            "text-field": ["get", "Text"],
            "text-anchor": "top",
            "text-radial-offset": 0.5,
            "text-justify": "auto",
            "text-size": 12
          },
          "paint": {
            "text-color": "#6b6b6b",
            "text-translate": [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              ["literal", [0, 4]],
              22,
              ["literal", [0, 16]],
            ],
            "text-halo-color": "rgb(255,255,255)",
            "text-halo-width": 1,
            "text-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              15,
              0,
              16,
              0.72
            ],
          }
        });
        this.setLayerAvailability({ loaded: 'ovkasten', status: true })
      }

      // Parking Spaces
      if (this.layerConfig.parking.active) {
        this.$store.map.addSource('parking', {
          type: 'vector',
          url: this.layerConfig.parking.url
        })
        this.$store.map.addLayer({ 
          "id": "parking",
          "type": "fill",
          "source": "parking",
          "source-layer": this.layerConfig.parking.source,
          "minzoom": 13,
          "paint": {
            "fill-color": "hsla(23,100%, 62%, 0.7)", // "hsl(52, 73%, 73%)",
            "fill-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              0,
              14,
              0.90
            ]
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'parking', status: true })
      }

      // Trees on top
      if (this.layerConfig.trees.active) {
        this.$store.map.addSource('trees', {
          "type": "vector",
          "url": this.layerConfig.trees.url
        })
        this.$store.map.addLayer({ 
          "id": "trees",
          "type": "circle",
          "source": "trees",
          "source-layer": this.layerConfig.trees.source,
          "minzoom": 13,
          "paint": {
            'circle-radius': [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              1,
              22,
              10
            ],
            "circle-color": "hsl(93, 28%, 43%)",
            "circle-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              0,
              14,
              0.40
            ],
            "circle-stroke-width": 0,
          }
        }, firstSymbolId)
      }

      // visitorlocations on top - there are a lot
      if (this.layerConfig.visitorlocations.active) {
        this.$store.map.addSource('visitorlocations', {
          "type": "vector",
          "url": this.layerConfig.visitorlocations.url
        })
        this.$store.map.addLayer({ 
          "id": "visitorlocations",
          "type": "circle",
          "source": "visitorlocations",
          "source-layer": this.layerConfig.visitorlocations.source,
          "minzoom": 13,
          // "layout": {
          //   "symbol-placement": "point",
          //   "symbol-z-order": "source",
          //   "icon-allow-overlap": true,
          //   "icon-image": "marker-15",
          // },
          // "paint": {
          //   // "icon-color": "",
          //   "icon-opacity": [ // Same as trees
          //     "interpolate",
          //     ["linear"],
          //     ["zoom"],
          //     13,
          //     0,
          //     14,
          //     0.72
          //   ]
          // }
          "paint": {
            'circle-radius': [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              1,
              22,
              10
            ],
            "circle-color": "hsl(0, 98%, 69%)",
            "circle-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              0,
              14,
              0.85
            ],
            "circle-stroke-width": 0,
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'visitorlocations', status: true })
      }

      // Park&Ride
      if (this.layerConfig.pplusr.active) {
        this.$store.map.addSource('pplusr', {
          "type": "vector",
          "url": this.layerConfig.pplusr.url
        })
        this.$store.map.addLayer({ 
          "id": "pplusr",
          "type": "symbol",
          "source": "pplusr",
          "source-layer": this.layerConfig.pplusr.source,
          "minzoom": 10,
          "layout": {
            "symbol-placement": "point",
            "symbol-z-order": "source",
            "icon-allow-overlap": true,
            "icon-image": "parkandride",  // "charging-station-15",
            "icon-size": 0.8
          },
          "paint": {
            // "icon-color": "",
            "icon-opacity": [ // transition based on zoom
              "interpolate",
              ["linear"],
              ["zoom"],
              10,
              0,
              10.2,
              0.8
            ] 
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'pplusr', status: true })
      }

      // Fuel on top
      if (this.layerConfig.fuelstations.active) {

        this.$store.map.addSource('fuelstations', {
          "type": "vector",
          "url": this.layerConfig.fuelstations.url
        })
        this.$store.map.addLayer({ 
          "id": "fuelstations",
          "type": "symbol",
          "source": "fuelstations",
          "source-layer": this.layerConfig.fuelstations.source,
          "minzoom": 10,
          "layout": {
            "symbol-placement": "point",
            "symbol-z-order": "source",
            "icon-allow-overlap": true,
            "icon-image": "fuelpump",
            "icon-size": 0.8
          },
          "paint": {
            // "icon-color": "",
            "icon-opacity": [ // transition based on zoom
              "interpolate",
              ["linear"],
              ["zoom"],
              10,
              0,
              10.2,
              0.8
            ] 
          }
        }, firstSymbolId)
        this.setLayerAvailability({ loaded: 'fuelstations', status: true })
      }


      // chargingpoints on top
      if (this.layerConfig.chargingpoints.active) {

        
        if (!this.$store.map.hasImage('chargingstation-inprogress')) {
          this.$store.map.loadImage(this.image({ name: 'chargingstation-inprogress.svg' }), (error, image) => {
            this.$store.map.addImage('chargingstation-inprogress', image)
          })
        }

        this.$store.map.addSource('chargingpoints', {
          "type": "vector",
          "url": this.layerConfig.chargingpoints.url
        })
        this.$store.map.addLayer({ 
          "id": "chargingpoints",
          "type": "symbol",
          "source": "chargingpoints",
          "source-layer": this.layerConfig.chargingpoints.source,
          "minzoom": 10,
          "layout": {
            "symbol-placement": "point",
            "symbol-z-order": "source",
            "icon-allow-overlap": true,
            
            "icon-image": ["match", 
              ['get', 'type'], 
              1, "chargingstation-inprogress",
              "chargingstation2-bestaand"],
            "icon-size": 0.8
          },
          "paint": {
            // "icon-color": "",
            "icon-opacity": [ // transition based on zoom
              "interpolate",
              ["linear"],
              ["zoom"],
              10,
              0,
              10.2,
              0.8
            ] 
          }
        }, firstSymbolId)
        this.setLayerAvailability({ layer: 'chargingpoints', status: true })
      }
    },
    // We want to add our layers below the labels
    // https://docs.mapbox.com/mapbox-gl-js/example/geojson-layer-in-stack/
    getFirstSymbolLayerId() {
      let layers = this.$store.map.getStyle().layers;
      // Find the index of the first symbol layer in the map style
      let firstSymbolId;
      for (let i = 0; i < layers.length; i++) {
        if (layers[i].type === 'symbol') {
          firstSymbolId = layers[i].id;
          break;
        }
      }

      return firstSymbolId
    },
    /**
     * This method generates the core element of the paint instructionset to 
     * paint the hexagons by the data contained in the hexagon layer.
     * 
     * The output is based on the 3 filters available to the user
     * 
     * The forecast param is 1 filter input. This one is passed in like this 
     * because for one variation we need to combine all other forecast 
     * variations generated by this method.
     */
    forecastFilterSwitch({ forecast }) {

      // Two of the filters.
      let year = this.getForecastStatus.year
      let access = this.getForecastStatus.access

      // Data fields in layer
      let fields = {
        bewonersD1: ['coalesce', [
          "get",
          "BewonersD1"
        ], 0],
        bewonersD2: ['coalesce', [
          "get",
          "BewonersD2"
        ], 0],
        bewonersD3: ['coalesce', [
          "get",
          "BewonersD3"
        ], 0],
        bewonersD4: ['coalesce', [
          "get",
          "BewonersD4"
        ], 0],
        bewonersD5: ['coalesce', [
          "get",
          "BewonersD5"
        ], 0],
        visit: ['coalesce', [
          "get",
          "Bezoekers"
        ], 0],
        commuter: ['coalesce', [
          "get",
          "Forenzen"
        ], 0],
        parking: ['coalesce', [
          "get",
          "Pubparkeer"
        ], 0]
      }

      switch (forecast + '-' + access) {

        // Public - Inhabitants
        case 'inhabitants-public': 
          return [
            "*", 
              ["+", 
                ["*", fields.bewonersD1, decielen.D1[year]],
                ["*", fields.bewonersD2, decielen.D2[year]],
                ["*", fields.bewonersD3, decielen.D3[year]],
                ["*", fields.bewonersD4, decielen.D4[year]],
                ["*", fields.bewonersD5, decielen.D5[year]],
              ], 
              fields.parking]

        // Private - Inhabitants
        case 'inhabitants-private': 
          return [
            "*", 
              ["+", 
                ["*", fields.bewonersD1, decielen.D1[year]],
                ["*", fields.bewonersD2, decielen.D2[year]],
                ["*", fields.bewonersD3, decielen.D3[year]],
                ["*", fields.bewonersD4, decielen.D4[year]],
                ["*", fields.bewonersD5, decielen.D5[year]],
              ], 
              ["-", 1, fields.parking]]

        // Public - visitors
        case 'visitors-public': 
          return ["*", fields.visit, fields.parking, decielen.D4[year]]

        // Private - visitors
        case 'visitors-private': 
          return ["*", fields.visit, ["-", 1, fields.parking], decielen.D4[year]]

        // Public - commuters
        case 'commuters-public': 
          return ["*", fields.commuter, fields.parking, decielen.D4[year]]

        // Private - commuters
        case 'commuters-private': 
          return ["*", fields.commuter, ["-", 1, fields.parking], decielen.D4[year]]

        // Determinative
        case 'determinative-public': 
        case 'determinative-private':
          return ["max", 
            this.forecastFilterSwitch({ forecast: 'inhabitants' }),
            this.forecastFilterSwitch({ forecast: 'visitors' }),
            this.forecastFilterSwitch({ forecast: 'commuters' })
          ]
      }
      
      // Default
      return 0
    }
  }
}
</script>

<style lang="scss">
  .mapboxgl-canvas-container, .mapboxgl-canvas {
    &:active, &:focus {
      outline: none;
    }
  }

  .mapboxgl-marker {
    position: absolute;
    cursor: pointer;
  }

  .mapboxgl-map {
    width: 100% !important;
    height: 100% !important;
  }

  .MapBox, .MapBox__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    user-select: none;
  }
  
  .MapBox__data {
    position: absolute;
    bottom: 2rem;
    right: 1rem;
  }

</style>
